import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WINDOWS = [
  { label: "1h",  hours: 1 },
  { label: "24h", hours: 24 },
  { label: "7d",  hours: 24 * 7 },
  { label: "30d", hours: 24 * 30 },
];

function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const s = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${Math.floor(s)}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function timeUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  const s = (d.getTime() - Date.now()) / 1000;
  if (s <= 0) return "thawing";
  if (s < 60) return `${Math.floor(s)}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
  return `${Math.floor(s / 86400)}d ${Math.floor((s % 86400) / 3600)}h`;
}

export default function OwnerAuditPanel() {
  const [hours, setHours] = useState(24);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/admin/token-audit`, {
        params: { hours, limit: 50 },
        withCredentials: true,
      });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Audit fetch failed.");
    } finally {
      setBusy(false);
    }
  }, [hours]);

  async function toggleFreeze(target, resetTokens = false) {
    const next = !target.frozen;
    if (next && !window.confirm(
      `Freeze ${target.user_name}? They will earn 0 tokens from any action until unfrozen.` +
      (resetTokens ? "\n\nAlso wiping their current balance to 0." : "")
    )) return;
    try {
      await axios.post(`${API}/admin/flag-user`, {
        user_id: target.user_id,
        frozen: next,
        reset_tokens: resetTokens,
      }, { withCredentials: true });
      toast.success(`${target.user_name} ${next ? "frozen 🧊" : "unfrozen 🔥"}${resetTokens ? " + balance wiped" : ""}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Flag failed.");
    }
  }

  useEffect(() => { load(); }, [load]);

  return (
    <div
      data-testid="owner-audit-panel"
      className="bg-[#0F0F0F] border border-[#D4AF37]/40 p-6 mt-6"
    >
      <div className="flex items-end justify-between gap-4 flex-wrap mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-1">
            Owner Tools · Token Audit
          </div>
          <h2 className="font-display text-2xl text-white">
            Phoenix Token <span className="text-[#D4AF37]">Ledger.</span>
          </h2>
          <p className="text-xs text-neutral-500 mt-1">
            Spot suspicious earn patterns. Newest first.
          </p>
          {data?.auto_flag_rule && (
            <p
              data-testid="auto-flag-rule"
              className="text-[10px] text-red-400 mt-1 uppercase tracking-widest"
            >
              Auto-flag: &gt; {data.auto_flag_rule.threshold} Phoenix Tokens in {data.auto_flag_rule.window_hours}h → frozen
              {data.auto_flag_rule.thaw_hours
                ? ` · auto-thaws after ${data.auto_flag_rule.thaw_hours}h`
                : ""}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {WINDOWS.map((w) => (
            <button
              key={w.label}
              data-testid={`audit-window-${w.label}`}
              onClick={() => setHours(w.hours)}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest border transition-colors ${
                hours === w.hours
                  ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10"
                  : "border-zinc-800 text-neutral-500 hover:border-[#D4AF37] hover:text-[#D4AF37]"
              }`}
            >
              {w.label}
            </button>
          ))}
          <button
            data-testid="audit-refresh"
            onClick={load}
            disabled={busy}
            className="px-3 py-1.5 text-xs font-bold uppercase tracking-widest border border-zinc-800 text-white hover:border-white transition-colors disabled:opacity-50"
          >
            {busy ? "..." : "Refresh"}
          </button>
        </div>
      </div>

      {!data ? (
        <p className="text-sm text-neutral-500 py-6 text-center">Loading...</p>
      ) : (
        <>
          {/* Top earners */}
          <div className="mb-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-2">
              Top earners (last {data.window_hours}h)
            </div>
            {data.top_earners.length === 0 ? (
              <p className="text-xs text-neutral-600 py-3">No token activity in this window. ✅</p>
            ) : (
              <div className="border border-zinc-900 divide-y divide-zinc-900">
                {data.top_earners.map((e, i) => (
                  <div
                    key={e.user_id}
                    data-testid={`audit-earner-${i}`}
                    className="flex items-center gap-3 px-3 py-2 hover:bg-[#0A0A0A] transition-colors"
                  >
                    <div className="font-mono text-xs text-neutral-600 w-6">{i + 1}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white truncate flex items-center gap-1.5">
                        {e.frozen && <span title={e.auto_flag_reason || "Frozen — earns 0 tokens"}>🧊</span>}
                        {e.user_name}
                        {e.auto_flag_reason && (
                          <span
                            data-testid={`audit-auto-tag-${i}`}
                            className="text-[9px] uppercase tracking-widest text-red-400 border border-red-900 px-1.5 py-0.5"
                            title={e.auto_flag_reason}
                          >
                            Auto
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-neutral-500 truncate">{e.user_email}</div>
                      {e.auto_flag_reason && (
                        <div className="text-[10px] text-red-400/80 truncate" title={e.auto_flag_reason}>
                          {e.auto_flag_reason}
                          {e.thaw_at && (
                            <span className="ml-1 text-blue-400" data-testid={`audit-thaw-${i}`}>
                              · thaws in {timeUntil(e.thaw_at)}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="font-mono tabular-nums font-bold text-[#D4AF37] text-sm">
                        +{e.window_earned} <span className="text-[10px] text-neutral-500">Phoenix Tokens</span>
                      </div>
                      <div className="text-[10px] text-neutral-500">
                        {e.window_events} earns · total {e.current_total}
                      </div>
                    </div>
                    <div className="flex gap-1.5 ml-2">
                      <button
                        data-testid={`audit-freeze-${i}`}
                        onClick={() => toggleFreeze(e, false)}
                        className={`px-2 py-1 text-[10px] font-bold uppercase tracking-widest border transition-colors ${
                          e.frozen
                            ? "border-blue-500 text-blue-400 hover:bg-blue-500/10"
                            : "border-zinc-700 text-neutral-400 hover:border-red-500 hover:text-red-400"
                        }`}
                        title={e.frozen ? "Unfreeze — let them earn again" : "Freeze — stop all token earning"}
                      >
                        {e.frozen ? "Unfreeze" : "Freeze"}
                      </button>
                      {!e.frozen && (
                        <button
                          data-testid={`audit-freeze-reset-${i}`}
                          onClick={() => toggleFreeze(e, true)}
                          className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest border border-zinc-700 text-neutral-400 hover:border-red-700 hover:text-red-500 transition-colors"
                          title="Freeze AND zero their balance"
                        >
                          F+0
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* By source */}
          {data.by_source.length > 0 && (
            <div className="mb-6">
              <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-2">
                By source
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {data.by_source.map((s) => (
                  <div
                    key={s.source}
                    data-testid={`audit-source-${s.source}`}
                    className="bg-[#0A0A0A] border border-zinc-900 p-2"
                  >
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500 truncate">
                      {s.source}
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="font-mono tabular-nums font-bold text-[#D4AF37]">
                        +{s.total}
                      </span>
                      <span className="text-[10px] text-neutral-500">×{s.count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent ledger */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-2">
              Recent earns (latest 50)
            </div>
            {data.rows.length === 0 ? (
              <p className="text-xs text-neutral-600 py-3">No earns in this window.</p>
            ) : (
              <div className="border border-zinc-900 divide-y divide-zinc-900 max-h-[420px] overflow-y-auto">
                {data.rows.map((r, i) => (
                  <div
                    key={i}
                    data-testid={`audit-row-${i}`}
                    className="grid grid-cols-12 gap-2 px-3 py-2 text-xs items-center hover:bg-[#0A0A0A] transition-colors"
                  >
                    <div className="col-span-4 truncate">
                      <div className="text-white truncate">{r.user_name}</div>
                      <div className="text-[10px] text-neutral-600 truncate">{r.user_email}</div>
                    </div>
                    <div className="col-span-3 text-neutral-400 truncate">{r.source}</div>
                    <div className="col-span-2 font-mono tabular-nums text-[#D4AF37] font-bold">
                      +{r.amount}
                    </div>
                    <div className="col-span-3 text-right text-neutral-500 font-mono text-[10px]">
                      {timeAgo(r.timestamp)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
